/**
 * SidePanel Component
 *
 * Simple container for the Assistant view.
 * Can be minimized to a rail showing an icon button.
 * The header with tabs and actions is managed by ChatSidebar.
 */

import React, { useCallback } from 'react';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PanelRail } from './PanelRail';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    position: 'relative',
    zIndex: 1002, // Above NodeEditorModal (z-index: 1001) to remain visible when editor opens
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
});

export type SidePanelTab = 'code' | 'chat';

export interface SidePanelProps {
  /** Currently active tab, or null if panel should be closed */
  activeTab: SidePanelTab | null;
  /** Callback when expand is requested (from minimized rail) */
  onTabChange: (tab: SidePanelTab | null) => void;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Content to render for the Chat/AI tab */
  chatContent: React.ReactNode;
  /** Whether the panel is minimized to a rail */
  isMinimized?: boolean;
  /** Callback when minimize button is clicked */
  onMinimize?: () => void;
  /** Whether the AI tab has unread messages (shown as badge when minimized) */
  hasUnreadMessages?: boolean;
}

export const SidePanel: React.FC<SidePanelProps> = ({
  activeTab,
  onTabChange,
  onClose,
  chatContent,
  isMinimized = false,
  onMinimize,
  hasUnreadMessages = false,
}) => {
  const styles = useStyles();

  const handleExpand = useCallback(
    () => {
      onTabChange('chat');
    },
    [onTabChange]
  );

  // Don't render if no active tab and not minimized
  if (!activeTab && !isMinimized) {
    return null;
  }

  // Render rail when minimized (Assistant only)
  if (isMinimized) {
    return (
      <PanelRail
        onExpand={() => handleExpand()}
        hasUnreadMessages={hasUnreadMessages}
        hideCode={true}
      />
    );
  }

  // Clone chatContent to pass onClose and onMinimize props
  const chatContentWithProps = React.isValidElement(chatContent)
    ? React.cloneElement(chatContent as React.ReactElement<{ onClose?: () => void; onMinimize?: () => void }>, {
        onClose,
        ...(onMinimize ? { onMinimize } : {}),
      })
    : chatContent;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {chatContentWithProps}
      </div>
    </div>
  );
};

export default SidePanel;
